import { createPortal } from 'react-dom';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Spinner } from 'src/components/ui/spinner';
import ApiError from 'src/api/ApiError';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import { usePageReady } from 'src/hooks/usePageReady';
import {
  useCanClaimHeaderTitle,
  useHeaderTitleSlot,
} from 'src/context/header-title.hooks';

type PageProps = React.PropsWithChildren<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  isLoading?: boolean;
  error?: Error | ApiError;
  actions?: React.ReactNode;
}>;

const Page: React.FC<PageProps> = ({
  title,
  subtitle,
  isLoading,
  error,
  actions,
  children,
}) => {
  // `isLoading === false` rather than `!isLoading`: the prop is optional, so a
  // page that never passes it would otherwise report ready on first render —
  // measuring "mounted" instead of "usable". Omitting it now leaves manual
  // readiness unsignalled, which surfaces as a loud `timeout` outcome rather
  // than a silently wrong duration.
  usePageReady({
    ready: isLoading === false,
    outcome: error ? 'error' : 'success',
  });

  // The title lives in the header breadcrumb, replacing the crumb that would
  // otherwise repeat it. Outside the app shell (tests, stories) it has no
  // header to go to and renders inline.
  const canClaimTitle = useCanClaimHeaderTitle();
  const inlineTitle = title && !canClaimTitle;
  const showHeaderRow = inlineTitle || subtitle || actions;

  return (
    <DashboardLayout>
      {title && !inlineTitle && <PageHeaderTitle title={title} />}
      <div className="p-1">
        {showHeaderRow && (
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              {inlineTitle && (
                <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
              )}
              {subtitle ? (
                <p className="text-muted-foreground">{subtitle}</p>
              ) : undefined}
            </div>
            {actions && (
              <div className="flex items-center gap-2">{actions}</div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : error ? (
          <div className="flex justify-center p-3">
            <ErrorMessage error={error} />
          </div>
        ) : (
          children
        )}
      </div>
    </DashboardLayout>
  );
};

function PageHeaderTitle({ title }: { title: React.ReactNode }) {
  const titleSlot = useHeaderTitleSlot(true);
  return titleSlot
    ? createPortal(<h1 className="truncate">{title}</h1>, titleSlot)
    : null;
}

export default Page;
